import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { LocationStudy } from '../model/location-study.model';
import { SERVER_API_URL } from '../../../app.constants';
import { catchError } from 'rxjs/operators';
import { SkipErrorInterceptor } from '../../../blocks/interceptor/error.interceptor';
import { EnableLoadingIndicator, EnableSavingIndicator } from '../../loading-indicator';

@Injectable({
    providedIn: 'root'
})
export class LocationStudyService {

    constructor(private http: HttpClient) {
    }

    /**
     * Retrieves all studies for a given location.
     *
     * @param locId The location identifier
     * @returns Observable of LocationStudy array
     */
    getByLocationId(locId: number): Observable<LocationStudy[]> {
        return this.http.get<LocationStudy[]>(`${SERVER_API_URL}location-studies/location/${locId}`, { context: new HttpContext().set(SkipErrorInterceptor, true).set(EnableLoadingIndicator, true) }).pipe(
            catchError((error) => {
                if (error.status === 404) {
                    return of(null);
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Retrieves a specific study by location and geolocation IDs.
     *
     * @param locId The location identifier
     * @param geoId The geolocation identifier
     * @returns Observable of LocationStudy
     */
    getByLocationAndGeolocation(locId: number, geoId: number): Observable<LocationStudy | null> {
        return this.http.get<LocationStudy>(`${SERVER_API_URL}location-studies/location/${locId}/geolocation/${geoId}`, { context: new HttpContext().set(SkipErrorInterceptor, true).set(EnableLoadingIndicator, true) }).pipe(
            catchError((error) => {
                if (error.status === 404) {
                    return of(null);
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Retrieves a specific study geolocation IDs.
     *
     * @param locId The location identifier
     * @param geoId The geolocation identifier
     * @returns Observable of LocationStudy
     */
    getByGeolocation(geoId: number): Observable<LocationStudy | null> {
        return this.http.get<LocationStudy>(`${SERVER_API_URL}location-studies/location/geolocation/${geoId}`, { context: new HttpContext().set(SkipErrorInterceptor, true).set(EnableLoadingIndicator, true) }).pipe(
            catchError((error) => {
                if (error.status === 404) {
                    return of(null);
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Creates a new study location.
     *
     * @param study The LocationStudy to create
     * @returns Observable of created LocationStudy
     */
    createStudyLocation(study: LocationStudy): Observable<LocationStudy> {
        return this.http.post<LocationStudy>(`${SERVER_API_URL}location-studies`, study);
    }

    /**
     * Updates an existing study location.
     *
     * @param study The LocationStudy to update
     * @returns Observable of updated LocationStudy
     */
    updateStudyLocation(study: LocationStudy): Observable<LocationStudy> {
        if (!study.id) {
            throw new Error('Study ID is required for update');
        }
        return this.http.put<LocationStudy>(`${SERVER_API_URL}location-studies/${study.id}`, study);
    }

    /**
     * Retrieves a specific study by location and geolocation IDs.
     *
     * @param locId The location identifier
     * @param geoId The geolocation identifier
     * @returns Observable of LocationStudy
     */
    deleteByLocationAndGeolocation(locId: number, geoId: number): Observable<void> {
        return this.http.delete<void>(`${SERVER_API_URL}location-studies/location/${locId}/geolocation/${geoId}`);
    }
}
